"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  Dispatch,
} from "react";
import { toast } from "sonner";
import { Product } from "@/packages/types/products";
import { addToWishlist, fetchUserWishlist } from "@/api-service/wishlist";
import { WishlistItem } from "../types/wishlist";
import Cookies from "js-cookie";
import { useAuth } from "./AuthContext";

// --- TYPES ---

interface WishlistState {
  wishlistItems: WishlistItem[];
}

type WishlistAction =
  | {
      type: "INIT_WISHLIST";
      payload: WishlistItem[];
    }
  | {
      type: "TOGGLE_WISHLIST";
      payload: { productDetails: Product };
    };

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  dispatch: Dispatch<WishlistAction>;
  isInWishlist: (id: number | string) => boolean;
  toggleWishlist: (product: Product) => Promise<void>;
}

// --- CONTEXT ---

const WishlistContext = createContext<WishlistContextType | null>(null);

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
}

// --- INITIAL STATE ---

const initialState: WishlistState = {
  wishlistItems: [],
};

// --- UTILS ---

const getItemKey = (id: number | string) => `${id}`;

const updateWishlistStorage = (wishlistItems: WishlistItem[]) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("wishlistItems", JSON.stringify(wishlistItems));
  }
};

// --- REDUCER (pure, no side effects) ---

const wishlistReducer = (
  state: WishlistState,
  action: WishlistAction
): WishlistState => {
  switch (action.type) {
    case "INIT_WISHLIST":
      return { ...state, wishlistItems: action.payload };

    case "TOGGLE_WISHLIST": {
      const { productDetails } = action.payload;
      const key = getItemKey(productDetails.id);
      const existingIndex = state.wishlistItems.findIndex(
        (item) => getItemKey(item.id) === key
      );

      if (existingIndex !== -1) {
        const updatedItems = state.wishlistItems.filter(
          (_, index) => index !== existingIndex
        );
        updateWishlistStorage(updatedItems);
        return { ...state, wishlistItems: updatedItems };
      } else {
        const newItem: WishlistItem = {
          id: productDetails.id,
          createdAt: new Date().toISOString(),
          product: {
            id: productDetails.id,
            title: productDetails.title,
            stock: productDetails.stock,
            salePrice: productDetails.salePrice,
            comparePrice: productDetails.comparePrice,
            slug: productDetails.slug,
            averageRating: productDetails.averageRating,
            totalRating: productDetails.totalRating,
            variantStatus: productDetails.variantStatus,
            thumbnail: productDetails.thumbnail,
            warranty: productDetails.warranty,
            images: [],
            categoryId: productDetails.categoryId,
            categories: [],
            producttaxes: [],
            specialDiscountStatus: productDetails.specialDiscountStatus,
            specialDiscount: productDetails.specialDiscount || null,
          },
        };
        const updatedItems = [newItem, ...state.wishlistItems];
        updateWishlistStorage(updatedItems);
        return { ...state, wishlistItems: updatedItems };
      }
    }

    default:
      return state;
  }
};

// --- PROVIDER ---

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(wishlistReducer, initialState);
  const { accessToken, isLoggedIn } = useAuth();
  const cookieToken = Cookies.get("accessToken");
  // Prefer auth context token; fall back to cookie for safety (e.g. on hard refresh)
  const token = accessToken || cookieToken;
  const fetchWishlist = async () => {
    try {
      const response = await fetchUserWishlist();
      // const products = response.data.map((item) => ({
      //   id: item?.product?.id || item.id,
      //   title: item.product.title,
      // }));
      updateWishlistStorage(response.data);
      dispatch({ type: "INIT_WISHLIST", payload: response.data });
    } catch (err) {
      console.error("Failed to fetch wishlist:", err);
    }
  };
  // On mount: fetch wishlist from API
  useEffect(() => {
    if (token && isLoggedIn) {
      fetchWishlist();
    } else if (!isLoggedIn) {
      // Clear wishlist when user logs out
      updateWishlistStorage([]);
      dispatch({ type: "INIT_WISHLIST", payload: [] });
    }
  }, [token, isLoggedIn]);

  // Toggle wishlist item
  const toggleWishlist = async (product: Product) => {
    if (!token || !isLoggedIn) {
      toast.info("Please login to manage your wishlist");
      return;
    }
    const isAlreadyInWishlist = state.wishlistItems.some(
      (item) => item.product.id == product.id
    );

    try {
      if (!isAlreadyInWishlist) {
        await addToWishlist({ productId: product.id });

        fetchWishlist();

        toast.success("Added to wishlist");
      } else {
        await addToWishlist({ productId: product.id });
        fetchWishlist();

        toast.info("Removed from wishlist");
      }

      dispatch({
        type: "TOGGLE_WISHLIST",
        payload: { productDetails: product },
      });
    } catch (err) {
      console.error("Wishlist toggle failed:", err);
      toast.error("Failed to update wishlist");
    }
  };

  const isInWishlist = (id: number | string) =>
    state.wishlistItems.some((item) => item.product.id == id);

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems: state.wishlistItems,
        dispatch,
        isInWishlist,
        toggleWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
